import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Form from 'components/form'
import { useForm } from 'react-hook-form'
import Button from 'components/Button'
import Checkbox from 'components/form/Checkbox'
import Link from 'next/link'
import PublicModalDrawer from 'components/PublicModalDrawer'
import { ArrowRightIcon, FacebookIcon, GoogleStyledIcon } from 'components/icons'
import CustomerRegisterModal from 'components/modals/CustomerRegisterModal'
import { useMutation, useQuery } from 'react-query'
import ApiClient from 'data/api/api_client'
import { useDispatch } from 'react-redux'
import useErrorHandler from 'data/hooks/useErrorHandler'
import { setUser } from 'store/auth'
import queryCache from 'data/queryCache'
import CustomerForgottenPasswordModal from 'components/modals/CustomerForgottenPasswordModal'
import ModalNavControl from 'components/ModalNavControl'

const CustomerLoginModal = ({ isOpen = false, onClose = () => { } }) => {
  const dispatch = useDispatch()
  const handleError = useErrorHandler()

  const [registerModalOpen, setRegisterModalOpen] = useState(false)
  const [forgottenModalOpen, setForgottenModalOpen] = useState(false)

  const { data: socialLinks } = useQuery(
    queryCache.socialLinks,
    () => ApiClient.auth.getSocialLinks(),
  )

  const { register, handleSubmit, setError, formState: { errors }, reset, watch } = useForm()

  const loginMutation = useMutation(
    () => ApiClient.auth.login(watch('email'), watch('password'), watch('remember_me')),
    {
      onSuccess: (user) => {
        dispatch(setUser(user))
        onClose()
        reset()
      },
      onError: (error) => {
        handleError(error, setError)
      },
    },

  )

  const onRegisterClose = (closeLoginModal = false) => {
    setRegisterModalOpen(false)
    if (closeLoginModal) {
      onClose()
    }
  }

  const onForgottenClose = (closeLoginModal = false) => {
    setForgottenModalOpen(false)
    if (closeLoginModal) {
      onClose()
    }
  }

  return (
    <PublicModalDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col justify-start md:h-full md:justify-between z-30 gap-50 md:gap-0">
        <form onSubmit={handleSubmit(loginMutation.mutate)} className="flex flex-col">
          <h1 className="font-serif font-bold tracking-title leading-28 text-26 text-grey-800">
            Sign in to your account
          </h1>

          <label className="text-14 leading-20 mt-50 md:mt-80 font-sans text-grey-800">
            Email Address
            <Form.Input
              style="customer"
              className="mt-10"
              placeholder="Your email here"
              error={errors?.email?.message}
              {...register('email')}
            />
          </label>

          <label className="text-14 leading-20 mt-20 font-sans text-grey-800">
            Password
            <Form.Input
              style="customer"
              className="mt-10"
              placeholder="Enter your password"
              type="password"
              error={errors?.password?.message}
              {...register('password')}
            />
          </label>

          <Button
            type="submit"
            block
            className="mt-30 uppercase leading-20"
            isLoading={loginMutation.isLoading}
          >
            Login
          </Button>

          <div className="flex w-full justify-between items-center whitespace-nowrap mt-30 font-sans">
            <Checkbox
              className="leading-20"
              style="customer"
              error={errors?.remember_me?.message}
              {...register('remember_me')}
            >
              Remember me
            </Checkbox>

            <ModalNavControl.Button
              text="Forgot Password"
              className="text-grey-500 leading-20 text-14 hover:underline"
              onClick={() => setForgottenModalOpen(true)}
            />
          </div>
        </form>

        <div>
          <Link className="no-underline" href={socialLinks?.facebook ?? '#'}>
            <Button
              block={true}
              variant="outline-grey"
              className="flex items-center justify-between group mt-20 hover:bg-white hover:!border-primary px-16 py-8 h-[52px] !font-roboto"
            >
              <div className="flex items-center">
                <FacebookIcon className="text-20 text-[#1877F2]" />
                <span className="text-14 ml-[32px] text-grey-800 normal-case leading-20 !font-medium">Sign in with Facebook</span>
              </div>
              <ArrowRightIcon className="text-grey-800 text-opacity-40 group-hover:text-opacity-100" />
            </Button>
          </Link>

          <Link className="no-underline" href={socialLinks?.google ?? '#'}>
            <Button
              block={true}
              variant="outline-grey"
              className="flex items-center justify-between group mt-20 hover:bg-white hover:!border-primary px-16 py-8 h-[52px] !font-roboto"
            >
              <div className="flex items-center">
                <GoogleStyledIcon className="text-20" />
                <span className="text-14 ml-[32px] text-grey-800 normal-case leading-20 !font-medium">Sign in with Google</span>
              </div>
              <ArrowRightIcon className="text-grey-800 text-opacity-40 group-hover:text-opacity-100" />
            </Button>
          </Link>

          <p className="text-center text-grey-500 text-14 mt-30 leading-20">
            {'Don\'t have an account yet? '}
            <button
              className="ml-5 text-primary focus:outline-none focus:text-primary-active underline cursor-pointer font-bold leading-20"
              onClick={() => setRegisterModalOpen(true)}
            >
              Sign up now
            </button>
          </p>
        </div>
      </div>

      <CustomerForgottenPasswordModal isOpen={forgottenModalOpen} onClose={onForgottenClose} />
      <CustomerRegisterModal isOpen={registerModalOpen} onClose={onRegisterClose} />
    </PublicModalDrawer>
  )
}

CustomerLoginModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
}

export default CustomerLoginModal
